import PropTypes from 'prop-types'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectQuote } from '../../../../reducers/programRegistrationReducer'
import { displayWithCurrency } from '../../../../utility/currency'
import Tooltip from '../../../common/Tooltip'

import './MembershipToday.scss'
import { ReactComponent as InfoCircleIcon } from '../../../../assets/images/common/icons/info-circle-icon-outline.svg'

const MembershipToday = ({ plan }) => {
  const { t } = useTranslation()
  const { membership } = useSelector(selectQuote)
  const selectedFamilyMember = useSelector(state => state.programRegistration.selectedFamilyMember)

  const tierName = tier => t(`programs.registration.membershipPlan.tiers.${tier}`)
  const perYear = (amount, currency) => t('programs.registration.membershipPlan.perYear', { price: displayWithCurrency(amount, currency, true) })
  const [holder] = membership.current.seatHolders
  const dates = {
    purchasedOn: moment(membership.current.purchasedAt).format('ll'),
    periodEnd: moment(membership.current.currentPeriodEnd).format('ll'),
  }

  const purchased = membership.current.seats === 1 && holder
    ? t(
      membership.current.autoRenew
        ? 'programs.registration.membershipToday.heldByRenewing'
        : 'programs.registration.membershipToday.heldByEnding',
      { ...dates, name: `${holder.firstName} ${holder.lastName}` }
    )
    : t(
      membership.current.autoRenew
        ? 'programs.registration.membershipToday.heldForRenewing'
        : 'programs.registration.membershipToday.heldForEnding',
      { ...dates, count: membership.current.seats }
    )

  return (
    <div id='MembershipToday'>
      <p className='membership-today-title'>{t('programs.registration.membershipToday.title')}</p>
      <div className='membership-today-row'>
        <span className='d-flex flex-wrap align-items-center current-plan'>
          <span className='current-plan-name'>
            {t('programs.registration.membershipToday.membership', { tier: tierName(membership.current.tier) })}
          </span>
          <span className='current-plan-status'>{t(`programs.registration.membershipToday.status.${membership.current.status}`)}</span>
        </span>
        <span className='current-plan-price'>{perYear(membership.current.amount, membership.current.currency)}</span>
      </div>
      <p className='membership-today-note'>{purchased}</p>
      <p className='membership-today-note'>
        {t('programs.registration.membershipToday.requires', {
          name: `${selectedFamilyMember.firstName} ${selectedFamilyMember.lastName}`,
          tier: tierName(membership.plans[0].tier),
        })}
      </p>
      <div className='membership-today-breakdown'>
        <div className='membership-today-row'>
          <span>
            {t('programs.registration.membershipToday.newPlan', { tier: tierName(plan.tier), count: plan.seats })}
            <InfoCircleIcon
              className='covered-info-icon'
              aria-hidden='true'
              data-tip={t('programs.registration.membershipToday.covers', {
                names: plan.previewCovered.map(golfer => `${golfer.firstName} ${golfer.lastName}`).join(', '),
              })}
              data-for='coveredGolfers'
            />
          </span>
          <span className='breakdown-price'>{perYear(plan.amount, plan.currency)}</span>
        </div>
        <div className='membership-today-row'>
          <span>{t('programs.registration.membershipToday.credit', { tier: tierName(membership.current.tier) })}</span>
          <span className='breakdown-price'>
            {t('programs.registration.membershipToday.creditAmount', {
              price: displayWithCurrency(membership.current.amount, membership.current.currency, true),
            })}
          </span>
        </div>
      </div>
      <div className='membership-today-row upgrade-cost'>
        <span>{t('programs.registration.membershipToday.upgradeCost')}</span>
        <span className='upgrade-cost-price'>{displayWithCurrency(plan.upgradeAmount, plan.currency, true)}</span>
      </div>
      <Tooltip id='coveredGolfers' />
    </div>
  )
}

MembershipToday.propTypes = {
  plan: PropTypes.shape({
    tier: PropTypes.string,
    seats: PropTypes.number,
    amount: PropTypes.number,
    upgradeAmount: PropTypes.number,
    currency: PropTypes.object,
    previewCovered: PropTypes.arrayOf(PropTypes.shape({ firstName: PropTypes.string, lastName: PropTypes.string })),
  }).isRequired,
}

export default MembershipToday
